/**
 * @file modalPanel.ts
 *
 * Helpers for a modal panel that runs its own buttons. A panel whose buttons
 * only ever appear inside it wires them here rather than adding their names to
 * the global as-button-click switch in as-app, so the names stay private to the
 * component. Where an action needs the app, the panel sends an `as-intrn-`
 * event and lets the owning mixin do the work.
 */
import type { AsModalOverlay } from './as-modal-overlay.ts';

/** The overlay a panel sits in. as-app declares its panels inside the overlay,
 *  so `closest` finds it; the id covers a panel created and appended later. */
export const panelOverlay = (panel: HTMLElement): AsModalOverlay | null =>
  (panel.closest('as-modal-overlay') ?? document.querySelector('#as-app-overlay')) as AsModalOverlay | null;

/** Open the overlay on this panel. */
export const openPanel = (panel: HTMLElement, id = panel.id): void => {
  panelOverlay(panel)?.open(id);
};

/** Close the overlay, but only while this panel is the one on show, so a
 *  request that lands late can't dismiss whatever took the overlay over in the
 *  meantime. */
export const closePanel = (panel: HTMLElement, id = panel.id): void => {
  const overlay = panelOverlay(panel);
  if (overlay?.visibleChildId === id) overlay.close();
};

/** Shadow roots already wired, so a panel that reconnects doesn't stack a
 *  second listener and run every handler twice. */
const wired = new WeakSet<ShadowRoot>();

/**
 * Route the panel's own `as-button-click` events by button name. Listens on the
 * panel's shadow root, so only its own buttons reach it. Call from
 * connectedCallback, after the shadow root exists; calling again is a no-op.
 */
export const onPanelButtons = (
  panel: HTMLElement,
  handlers: Record<string, (event: CustomEvent) => void>,
): void => {
  const root = panel.shadowRoot;
  if (!root || wired.has(root)) return;
  wired.add(root);
  root.addEventListener('as-button-click', (event: Event) => {
    handlers[(event as CustomEvent).detail?.buttonName]?.(event as CustomEvent);
  });
};
